import { db, firebase } from "../services/firebase/Config";

/**
 * Migrates clinical history from 'prestaciones' (embedded notes) to 'atenciones' and 'sesiones'.
 * @param {string} empresaId - The ID of the company to migrate.
 */
export const migrateClinicalHistory = async (empresaId, specificPacienteId = null) => {
    if (!empresaId && !specificPacienteId) {
        console.error("Migration: empresaId is required (or provide specificPacienteId).");
        return;
    }

    // Diagnostic: Check where prestaciones live
    try {
        console.log("Diagnostic: Checking one random prestacion in the DB...");
        const randomPres = await db.collectionGroup('prestaciones').limit(1).get();
        if (!randomPres.empty) {
            console.log("Diagnostic: Sample prestacion path found at:", randomPres.docs[0].ref.path);
        } else {
            console.warn("Diagnostic: No 'prestaciones' found in the ENTIRE database via collectionGroup.");
        }
    } catch (e) {
        console.error("Diagnostic check failed:", e);
    }

    console.log(`Starting migration... Empresa: ${empresaId}, Patient: ${specificPacienteId || 'ALL'}`);
    let stats = {
        pacientesProcessed: 0,
        prestacionesProcessed: 0,
        atencionesCreated: 0,
        sesionesCreated: 0,
        errors: 0,
    };

    try {
        let pacientesDocs = [];

        if (specificPacienteId) {
            const pDoc = await db.collection("pacientes").doc(specificPacienteId).get();
            if (pDoc.exists) {
                pacientesDocs = [pDoc];
            } else {
                console.error("Patient not found:", specificPacienteId);
                return;
            }
        } else {
            // 1. Fetch all patients for the company
            const pacientesSnapshot = await db
                .collection("pacientes")
                .where("empresaId", "==", empresaId)
                .get();

            if (pacientesSnapshot.empty) {
                console.warn("No patients found for this company.");
                return;
            }
            pacientesDocs = pacientesSnapshot.docs;
        }

        const totalPacientes = pacientesDocs.length;
        console.log(`Found ${totalPacientes} patients to process.`);

        // Iterate through each patient
        for (const docUsuario of pacientesDocs) {
            const pacienteId = docUsuario.id;
            // const pacienteData = docUsuario.data(); // Not strictly needed unless we want to log names
            stats.pacientesProcessed++;
            console.log(`Processing patient (${stats.pacientesProcessed}/${totalPacientes}): ${pacienteId}`);

            try {
                // 2. Fetch all prestaciones for this patient
                console.log(`Checking prestaciones for patient ID: ${pacienteId}`);
                const prestacionesSnapshot = await db
                    .collection("pacientes")
                    .doc(pacienteId)
                    .collection("prestaciones")
                    .get();

                if (prestacionesSnapshot.empty) {
                    console.warn(`No prestaciones found for patient ${pacienteId}`);
                    continue;
                }
                console.log(`Found ${prestacionesSnapshot.size} prestaciones for patient ${pacienteId}`);

                // Temporary storage for grouping notes by date
                // Map<DateString, Array<{ note, prestacionId, ... }>>
                // DateString format: YYYY-MM-DD
                const dailyActivity = new Map();

                prestacionesSnapshot.forEach((docPrestacion) => {
                    stats.prestacionesProcessed++;
                    const prestacionData = docPrestacion.data();
                    const prestacionId = docPrestacion.id;
                    const notasClinicas = prestacionData.notasClinicas || [];

                    // 3. Process notes
                    notasClinicas.forEach((notaObj) => {
                        // Structure of notaObj based on standard usage usually implies:
                        // { fecha: Timestamp | Date | string, contenido: string, ... }
                        // We need to robustly handle the date.

                        let noteDate = null;
                        if (notaObj.fecha) {
                            if (notaObj.fecha.toDate) {
                                noteDate = notaObj.fecha.toDate();
                            } else if (typeof notaObj.fecha === 'string' || typeof notaObj.fecha === 'number') {
                                noteDate = new Date(notaObj.fecha);
                            }
                        }

                        // If no valid date, fallback to prestacion creation date or skip?
                        // Let's fallback to prestacion fechaInicio if available, or fetch current if really broken.
                        if (!noteDate || isNaN(noteDate.getTime())) {
                            if (prestacionData.fechaInicio) {
                                if (prestacionData.fechaInicio.toDate) {
                                    noteDate = prestacionData.fechaInicio.toDate();
                                } else {
                                    noteDate = new Date(prestacionData.fechaInicio);
                                }
                            }
                        }

                        if (!noteDate || isNaN(noteDate.getTime())) {
                            // Extreme fallback: modification time or skip
                            console.warn(`Skipping note for prestacion ${prestacionId} in patient ${pacienteId} due to invalid date.`);
                            return;
                        }

                        const dateKey = `${noteDate.getFullYear()}-${(noteDate.getMonth() + 1).toString().padStart(2, '0')}-${noteDate.getDate().toString().padStart(2, '0')}`;

                        if (!dailyActivity.has(dateKey)) {
                            dailyActivity.set(dateKey, []);
                        }

                        dailyActivity.get(dateKey).push({
                            prestacionId: prestacionId,
                            nota: notaObj, // The original note object
                            actualDate: noteDate
                        });
                    });
                });

                // 4. Create Atenciones and Sesiones
                // Use batching if possible, but beware of 500 limit. 
                // We will process day by day.

                for (const [dateKey, activities] of dailyActivity.entries()) {
                    const batch = db.batch();

                    // Create ONE Atencion for this day
                    // We use the earliest time for start, and latest for end, or just the date boundaries.
                    const sortedActivities = activities.sort((a, b) => a.actualDate - b.actualDate);
                    const fechaInicio = sortedActivities[0].actualDate;
                    const fechaFin = sortedActivities[sortedActivities.length - 1].actualDate;

                    const atencionRef = db.collection("pacientes").doc(pacienteId).collection("atenciones").doc();

                    batch.set(atencionRef, {
                        pacienteId: pacienteId,
                        empresaId: empresaId,
                        fechaInicio: fechaInicio,
                        fechaFin: fechaFin,
                        estado: "CERRADA",
                        migrated: true,
                        createdAt: firebase.firestore.FieldValue.serverTimestamp()
                    });
                    stats.atencionesCreated++;

                    // Create Sesiones linked to this Atencion
                    activities.forEach(activity => {
                        const sesionRef = db.collection("pacientes").doc(pacienteId).collection("sesiones").doc();

                        // Extract clean note string from object if possible, or stringify if complex
                        // Usually notaObj is like { texto: "..." } or just a string or { note: "..." }
                        // Let's assume standard structure: Check 'texto', 'nota', 'contenido', or whole object.
                        let notaText = "";
                        if (typeof activity.nota === 'string') notaText = activity.nota;
                        else if (activity.nota.nota) notaText = activity.nota.nota;
                        else if (activity.nota.texto) notaText = activity.nota.texto;
                        else if (activity.nota.contenido) notaText = activity.nota.contenido;
                        else notaText = JSON.stringify(activity.nota);

                        batch.set(sesionRef, {
                            atencionId: atencionRef.id,
                            prestacionId: activity.prestacionId,
                            fecha: activity.actualDate,
                            nota: notaText,
                            estado: "REALIZADO",
                            migrated: true
                        });
                        stats.sesionesCreated++;
                    });

                    await batch.commit();
                }

            } catch (err) {
                console.error(`Error processing patient ${pacienteId}:`, err);
                stats.errors++;
            }
        }

    } catch (error) {
        console.error("Critical Migration Error:", error);
    }

    console.log("Migration Complete.");
    console.log(stats);
};
